import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { IssueTaxonomyKeys } from '@risksmart-app/shared/forms/issues/types';
import { Paperclip } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteObligation } from '@/hooks/mutations/obligation';
import { useGetObligationById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';

import useExporter from './useExporter';

type Props = {
  selectedTabId:
    | 'actions'
    | 'controls'
    | 'details'
    | 'impacts'
    | 'indicators'
    | 'linkedItems'
    | 'ratings'
    | IssueTaxonomyKeys;
};

const Page: FC<Props> = ({ selectedTabId }) => {
  const obligationId = useGetGuidParam('obligationId');

  const navigate = useNavigate();

  const detailsPath = useGetDetailPath(obligationId);
  const parentPath = useGetDetailParentPath(obligationId);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const { t } = useTranslation(['common']);
  const defaultTitle = t('obligations.fallback_title');

  const { data, error } = useGetObligationById({
    queryArgs: { id: obligationId },
  });
  if (error) {
    throw error;
  }
  const [obligation] = data?.obligation || [];

  const isExternalObligation =
    obligation?.ExternalId !== null && obligation?.ExternalId !== undefined;

  const tabs = useTabs({
    parent: obligation,
    parentType: Parent_Type_Enum.Obligation,
    hrefRoot: detailsPath,
  });
  const { deleteObligation, loading: deleting } = useDeleteObligation();
  const oversightEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );
  const internalAuditEnabled = useIsModuleEnabled('internal_audit_entity');
  const {
    hasPermission: canViewCompliance,
    loading: canViewComplianceLoading,
  } = useHasPermissionQuery('read:oversight_assessment', obligation);
  const {
    hasPermission: canViewInternalAudit,
    loading: canViewInternalAuditLoading,
  } = useHasPermissionQuery('read:internal_audit_report', obligation);
  const [exportItem, { loading: exporting }] = useExporter(
    obligationId,
    internalAuditEnabled &&
      canViewInternalAudit &&
      !canViewInternalAuditLoading,
    oversightEnabled && canViewCompliance && !canViewComplianceLoading
  );

  const onDelete = useDeleteResultNotification({
    entityName: t('obligations.entity_name'),
    asyncAction: async () => {
      // do nothing if obligation isn't present yet.
      if (!obligation) {
        return false;
      }
      await deleteObligation(obligation.Id);
      navigate(parentPath);

      return true;
    },
  });

  if (data?.obligation.length === 0) {
    throw new PageNotFound(`Obligation with id ${obligationId} not found`);
  }
  const title = obligation?.Title;
  const counter = obligation && `(O-${obligation.SequentialId})`;

  // Object-level toolbar (CUS-161) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save
  // state bridged from the active tab's form via useObjectToolbarFormStore). The
  // Delete action carries the same `delete:obligation` gate as the legacy
  // Permission and stays disabled for external obligations (parity).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: obligation?.Title || defaultTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      parentObject: obligation,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: t('obligations.delete_button'),
            iconName: 'trash-01',
            permission: 'delete:obligation',
            onClick: () => setIsDeleteModalVisible(true),
            disabled: isExternalObligation,
          },
        ],
      },
    }),
    [
      obligation,
      defaultTitle,
      counter,
      t,
      exportItem,
      exporting,
      formConfig,
      isExternalObligation,
    ]
  );

  return (
    <PageLayout
      title={title}
      meta={{
        title: defaultTitle,
        icon: isExternalObligation ? <Paperclip /> : undefined,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          <Permission
            permission={'delete:obligation'}
            parentObject={obligation}
          >
            <Button
              variant={'normal'}
              formAction={'none'}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
              disabled={isExternalObligation}
            >
              {t('obligations.delete_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={selectedTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Obligation}
        parent={obligation}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Obligation}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={obligation}
      />
      <DeleteModal
        loading={deleting}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('obligations.confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
